import axios from 'axios'

// Détection automatique de l'environnement
const getApiUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  
  // En production, utiliser l'API publique
  if (typeof window !== 'undefined' && window.location.hostname === 'flightcasefwi.com') {
    return 'https://api.flightcasefwi.com/api'
  }
  
  // En développement, utiliser localhost (Docker expose le backend sur 3002)
  return 'http://localhost:3002/api'
}

const API_BASE_URL = getApiUrl()

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor pour ajouter le token d'auth (admin ou client)
api.interceptors.request.use(
  (config) => {
    const unifiedToken = localStorage.getItem('user_token') || localStorage.getItem('customer_token') || localStorage.getItem('admin_token')

    const url = config.url || ''
    const isRentman = url.startsWith('/rentman')
    const isAuth = url.startsWith('/auth')
    const isPublicRentman = url === '/rentman/settings/public' || url === '/rentman/test-connection-public'
    const isCustomerRoute = url.startsWith('/customers') || url.startsWith('/api/customers') || url.startsWith('/users') || url.startsWith('/api/users')

    let tokenToUse = unifiedToken || null

    if (tokenToUse) {
      config.headers.Authorization = `Bearer ${tokenToUse}`
    } else {
      // S'assurer qu'on ne fuite pas un mauvais token
      delete config.headers.Authorization
    }

    // Forcer no-cache sur auth/me
    if (url.startsWith('/auth/me')) {
      config.headers['Cache-Control'] = 'no-store'
      config.headers['Pragma'] = 'no-cache'
      config.headers['Expires'] = '0'
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor pour gérer les erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const url = error?.config?.url || ''
    
    // Routes publiques qui peuvent retourner 401/403 normalement (ne pas déconnecter)
    const publicRoutes = [
      '/auth/login',
      '/auth/register',
      '/auth/forgot-password',
      '/auth/reset-password'
    ]
    const isPublicRoute = publicRoutes.some(route => url.includes(route))
    
    // Si erreur 401 (Unauthorized) ou 403 (Forbidden) et ce n'est pas une route publique
    if ((status === 401 || status === 403) && !isPublicRoute && typeof window !== 'undefined') {
      // Vérifier si un token existe (pour éviter de déconnecter si l'utilisateur n'était pas connecté)
      const hasToken = localStorage.getItem('user_token') || 
                       localStorage.getItem('customer_token') || 
                       localStorage.getItem('admin_token')
      
      if (hasToken) {
        // Token expiré ou invalide - déconnecter l'utilisateur
        localStorage.removeItem('user_token')
        localStorage.removeItem('customer_token')
        localStorage.removeItem('admin_token')
        localStorage.removeItem('user')
        localStorage.removeItem('admin')
        localStorage.removeItem('current_user')
        localStorage.removeItem('user_role')
        
        // Rediriger vers la page de connexion seulement si on n'y est pas déjà
        const currentPath = window.location.pathname
        if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
          // Sauvegarder la page actuelle pour redirection après connexion
          const redirectPath = currentPath !== '/' ? currentPath + window.location.search : '/catalogue-materiels'
          window.location.href = `/login?redirect=${encodeURIComponent(redirectPath)}`
        }
      }
    }
    
    return Promise.reject(error)
  }
)

// Auth endpoints
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
}

// Clients endpoints
export const clientsAPI = {
  create: (clientData) => api.post('/clients', clientData),
  getAll: (params = {}) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
}

// Categories endpoints
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
}

// Products endpoints
export const productsAPI = {
  getAll: (params = {}) => api.get('/produits', { params }),
  getById: (id, params = {}) => api.get(`/produits/${id}`, { params }),
}

// Reservations endpoints
export const reservationsAPI = {
  create: (reservationData) => api.post('/reservations', reservationData),
  getAll: (params = {}) => api.get('/reservations', { params }),
  getById: (id) => api.get(`/reservations/${id}`),
  updateStatus: (id, status) => api.patch(`/reservations/${id}`, { status }),
  delete: (id) => api.delete(`/reservations/${id}`),
}

// Emails endpoints
export const emailsAPI = {
  sendConfirmation: (reservationId) => api.post('/emails/send-confirmation', { reservation_id: reservationId }),
  sendNotification: (reservationId) => api.post('/emails/send-notification', { reservation_id: reservationId }),
}

// Rentman endpoints
export const rentmanAPI = {
  getCategories: () => api.get('/rentman/categories'),
  getProducts: (params = {}) => api.get('/rentman/products', { params }),
  getProduct: (id) => api.get(`/rentman/products/${id}`),
  getAvailability: (id, params = {}) => api.get(`/rentman/availability/${id}`, { params }),
  createReservation: (data) => api.post('/rentman/reservations', data),
  sync: () => api.post('/rentman/sync'),
  stopSync: () => api.post('/rentman/stop-sync'),
  testConnection: () => api.post('/rentman/test-connection'),
  getLogs: (params = {}) => api.get('/rentman/logs', { params }),
  clearLogs: () => api.delete('/rentman/logs'),
  getSettings: () => api.get('/rentman/settings'),
  saveSettings: (settings) => api.post('/rentman/settings', settings),
}

// Wrapper functions for easier use
export const apiService = {
  // Méthodes axios génériques
  get: (url, config) => api.get(url, config),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  delete: (url, config) => api.delete(url, config),
  patch: (url, data, config) => api.patch(url, data, config),

  // Auth
  login: authAPI.login,
  getMe: authAPI.getMe,
  logout: authAPI.logout,

  // Clients
  createClient: clientsAPI.create,
  getClients: clientsAPI.getAll,
  getClient: clientsAPI.getById,

  // Categories
  getCategories: categoriesAPI.getAll,
  getCategory: categoriesAPI.getById,

  // Products
  getProducts: productsAPI.getAll,
  getProduct: productsAPI.getById,

  // Reservations
  createReservation: reservationsAPI.create,
  getReservations: reservationsAPI.getAll,
  getReservation: reservationsAPI.getById,
  updateReservationStatus: reservationsAPI.updateStatus,
  deleteReservation: reservationsAPI.delete,

  // Emails
  sendConfirmationEmail: emailsAPI.sendConfirmation,
  sendNotificationEmail: emailsAPI.sendNotification,

  // Rentman
  getRentmanCategories: rentmanAPI.getCategories,
  getRentmanProducts: rentmanAPI.getProducts,
  getRentmanProduct: rentmanAPI.getProduct,
  getRentmanAvailability: rentmanAPI.getAvailability,
  createRentmanReservation: rentmanAPI.createReservation,
  syncRentman: rentmanAPI.sync,
  stopRentmanSync: rentmanAPI.stopSync,
  testRentmanConnection: rentmanAPI.testConnection,
  getRentmanLogs: rentmanAPI.getLogs,
  clearRentmanLogs: rentmanAPI.clearLogs,
  getRentmanSettings: rentmanAPI.getSettings,
  saveRentmanSettings: rentmanAPI.saveSettings,

  // Users (admin)
  getUsers: (params = {}) => api.get('/users', { params })
}

export default apiService
